/**
 * Generic Data Files Analytics component.
 *
 * Pages pass their config { endpoint, kpis: [], charts: [] }, and this
 * component fetches data, renders a KPI strip, a Recharts grid, and
 * optionally tidy Top-N tables.
 *
 * Chart spec types: 'pie', 'bar', 'bar-h', 'line', 'composed', 'table'
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { LoadingSpinner } from '../../components/layout/SharedComponents';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend,
} from 'recharts';
import { ArrowRight } from 'lucide-react';
import DrillDownSheet from './DrillDownSheet';

export const PALETTE = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];

export const fmtMoney = (v) => `$${(v || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
export const fmtMoney2 = (v) => `$${(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const fmtMoneyShort = (v) => v >= 1000000 ? `$${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v || 0}`;

export function StatCard({ icon: Icon, label, value, sub, accent = 'bg-zinc-100 text-zinc-600', testIdPrefix = 'stat', onClick }) {
  const interactive = typeof onClick === 'function';
  return (
    <div
      onClick={interactive ? onClick : undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      className={`rounded-xl border bg-card p-5 transition-shadow ${interactive ? 'cursor-pointer hover:shadow-md hover:border-indigo-300' : 'hover:shadow-sm'}`}
      data-testid={`${testIdPrefix}-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wider font-medium">{label}</div>
          <div className="text-2xl font-bold mt-1 tabular-nums text-zinc-900">{value}</div>
          {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
        </div>
        {Icon && <div className={`p-2 rounded-lg ${accent}`}><Icon className="h-4 w-4" /></div>}
      </div>
    </div>
  );
}

export function ChartCard({ title, subtitle, children, span = 'col-span-2', height = 'h-64', onClick }) {
  const interactive = typeof onClick === 'function';
  return (
    <div
      onClick={interactive ? onClick : undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      className={`rounded-xl border bg-card p-5 ${span} transition-shadow ${interactive ? 'cursor-pointer hover:shadow-md hover:border-indigo-300' : ''}`}
    >
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-zinc-900">{title}</h3>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
        {interactive && <ArrowRight className="h-3.5 w-3.5 text-zinc-400" />}
      </div>
      <div className={height}>{children}</div>
    </div>
  );
}

export function TidyTopList({ rows, valueKey = 'value', extraKey = null, extraLabel = '', moneyFormat = true, onRowClick }) {
  if (!rows || rows.length === 0) return <div className="h-full flex items-center justify-center text-xs text-muted-foreground">No data yet</div>;
  const maxValue = Math.max(...rows.map(r => r[valueKey] || 0));
  return (
    <div className="overflow-y-auto h-full">
      <table className="w-full text-sm">
        <tbody>
          {rows.map((row, i) => {
            const pct = maxValue > 0 ? Math.round(((row[valueKey] || 0) / maxValue) * 100) : 0;
            return (
              <tr
                key={row.name || i}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={`border-b border-zinc-100 last:border-0 transition-colors ${onRowClick ? 'hover:bg-indigo-50 cursor-pointer' : 'hover:bg-zinc-50'}`}
                data-testid={`drill-row-${row.name}`}
              >
                <td className="py-2 pr-3 w-6 text-xs text-zinc-400 tabular-nums text-right">{i + 1}.</td>
                <td className="py-2 pr-3 text-left">
                  <div className="font-medium text-zinc-900 truncate">{row.name}</div>
                  <div className="mt-1 h-1.5 rounded-full bg-zinc-100 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: PALETTE[i % PALETTE.length] }} />
                  </div>
                </td>
                {extraKey && (
                  <td className="py-2 pr-3 text-right text-xs text-zinc-500 tabular-nums whitespace-nowrap">
                    {row[extraKey]} {extraLabel}
                  </td>
                )}
                <td className="py-2 pl-3 text-right font-mono tabular-nums text-zinc-900 whitespace-nowrap">
                  {moneyFormat ? fmtMoney2(row[valueKey]) : (row[valueKey] || 0).toLocaleString()}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ChartFromSpec({ spec, data, onSliceClick }) {
  const rows = data?.[spec.dataKey] || [];
  if (rows.length === 0) {
    return <div className="h-full flex items-center justify-center text-xs text-muted-foreground">No data yet</div>;
  }
  const clickable = !!(spec.drillTo && onSliceClick);
  const handleClick = (clicked) => {
    if (!clickable || !clicked) return;
    onSliceClick(spec, clicked);
  };
  const cursorStyle = clickable ? { cursor: 'pointer' } : undefined;

  if (spec.kind === 'pie') {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={rows} dataKey="value" nameKey="name" cx="40%" cy="50%" outerRadius={75} innerRadius={42} paddingAngle={2} style={cursorStyle}>
            {rows.map((row, i) => (
              <Cell
                key={i}
                fill={PALETTE[i % PALETTE.length]}
                style={cursorStyle}
                onClick={clickable ? () => handleClick(row) : undefined}
              />
            ))}
          </Pie>
          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
          <Legend verticalAlign="middle" align="right" layout="vertical" iconSize={8} wrapperStyle={{ fontSize: 11 }}
            onClick={clickable ? (entry) => handleClick(rows.find(r => r.name === entry.value) || entry.payload) : undefined} />
        </PieChart>
      </ResponsiveContainer>
    );
  }
  if (spec.kind === 'bar-h') {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} layout="vertical" margin={{ top: 8, right: 16, left: 16, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} allowDecimals={false} />
          <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} width={120} />
          <Tooltip cursor={{ fill: '#fafafa' }} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
          <Bar dataKey="value" radius={[0, 6, 6, 0]} style={cursorStyle}>
            {rows.map((row, i) => (
              <Cell key={i} fill={PALETTE[(i + (spec.colorOffset || 0)) % PALETTE.length]} style={cursorStyle}
                onClick={clickable ? () => handleClick(row) : undefined} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  }
  if (spec.kind === 'bar') {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#71717a' }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} allowDecimals={false} />
          <Tooltip cursor={{ fill: '#fafafa' }} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
          <Bar dataKey="value" radius={[6, 6, 0, 0]} style={cursorStyle}>
            {rows.map((row, i) => (
              <Cell key={i} fill={PALETTE[(i + (spec.colorOffset || 0)) % PALETTE.length]} style={cursorStyle}
                onClick={clickable ? () => handleClick(row) : undefined} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  }
  if (spec.kind === 'line') {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={rows} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} allowDecimals={false} />
          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
          <Line type="monotone" dataKey="value" stroke="#10b981" strokeWidth={2.5}
            dot={{ r: 3, fill: '#10b981' }} activeDot={{ r: 5, onClick: (e, p) => handleClick(p?.payload || p) }}
            style={cursorStyle} />
        </LineChart>
      </ResponsiveContainer>
    );
  }
  if (spec.kind === 'table') {
    return <TidyTopList rows={rows} valueKey={spec.valueKey || 'value'} extraKey={spec.extraKey} extraLabel={spec.extraLabel} moneyFormat={spec.moneyFormat !== false} onRowClick={clickable ? handleClick : undefined} />;
  }
  return null;
}

export default function DataFilesAnalytics({ endpoint, kpis = [], charts = [], testIdPrefix = 'analytics', extraParams, parentRoute, onCardClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null); // { title, subtitle, fetchUrl, columns, linkTo }
  const navigate = useNavigate();

  // Default card click: invoke onCardClick callback if provided, else navigate
  // to parentRoute if provided. If neither is given, tiles/charts are not clickable.
  const defaultClick = onCardClick
    ? () => onCardClick()
    : parentRoute
    ? () => navigate(parentRoute)
    : undefined;

  // Stringify extraParams for the useEffect dep so a new object identity each render doesn't cause issues
  const extraParamsKey = JSON.stringify(extraParams || {});

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(endpoint, { params: extraParams || {} });
        setData(r.data);
      } catch (e) {
        // swallow
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, extraParamsKey]);

  const handleSliceClick = (spec, clicked) => {
    if (!spec.drillTo || !clicked) return;
    const drillTo = spec.drillTo;
    const value = clicked[drillTo.valueKey || 'name'];
    if (!value && value !== 0) return;
    // Fetch a generous page; filter client-side on a known field
    const params = new URLSearchParams({ ...(drillTo.params || {}), page_size: '500' });
    const sep = drillTo.resource.includes('?') ? '&' : '?';
    const fetchUrl = `${drillTo.resource}${sep}${params.toString()}`;
    setDrill({
      title: `${drillTo.title || spec.title}: ${value}`,
      subtitle: clicked.value != null ? `${clicked.value} record${clicked.value === 1 ? '' : 's'}` : undefined,
      fetchUrl,
      filter: drillTo.queryKey ? { key: drillTo.queryKey, value } : null,
      columns: drillTo.columns || [
        { key: 'name', label: 'Name' },
      ],
      linkTo: drillTo.route ? `${drillTo.route}?${drillTo.queryKey || 'q'}=${encodeURIComponent(value)}` : undefined,
      dataPath: drillTo.dataPath || 'data',
    });
  };

  if (loading) return <div className="py-12"><LoadingSpinner /></div>;
  if (!data) return <div className="py-12 text-center text-sm text-muted-foreground">No data yet.</div>;

  return (
    <div className="space-y-5">
      {/* KPI strip */}
      {kpis.length > 0 && (
        <div className={`grid grid-cols-2 md:grid-cols-${Math.min(kpis.length, 4)} gap-4`}>
          {kpis.map((k, i) => {
            const click = k.route ? () => navigate(k.route) : defaultClick;
            return (
              <StatCard
                key={i}
                icon={k.icon}
                label={k.label}
                value={k.format ? k.format(data.summary?.[k.key]) : (data.summary?.[k.key] ?? 0)}
                sub={k.subKey && data.summary?.[k.subKey] != null ? (k.subFormat ? k.subFormat(data.summary[k.subKey]) : `${data.summary[k.subKey]} ${k.subLabel || ''}`.trim()) : null}
                accent={k.accent}
                testIdPrefix={testIdPrefix}
                onClick={click}
              />
            );
          })}
        </div>
      )}

      {/* Chart rows */}
      {charts.map((row, ri) => (
        <div key={ri} className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {row.map((spec, ci) => {
            const click = spec.route ? () => navigate(spec.route) : defaultClick;
            return (
              <ChartCard
                key={ci}
                title={spec.title}
                subtitle={spec.subtitle}
                span={spec.span || 'md:col-span-2'}
                onClick={click}
              >
                <ChartFromSpec spec={spec} data={data} onSliceClick={handleSliceClick} />
              </ChartCard>
            );
          })}
        </div>
      ))}

      <DrillDownSheet
        open={!!drill}
        onClose={() => setDrill(null)}
        title={drill?.title}
        subtitle={drill?.subtitle}
        fetchUrl={drill?.fetchUrl}
        columns={drill?.columns || []}
        dataPath={drill?.dataPath}
        linkTo={drill?.linkTo}
        filter={drill?.filter}
      />
    </div>
  );
}
